import {
  BackHandler,
  LayoutChangeEvent,
  StyleProp,
  View,
  ViewStyle,
} from 'react-native';
import React, { useEffect } from 'react';

import { dismissReactNativeModal } from '../utilities/NativeHelpers';

const debug = require('debug')('tca:kit:navigation:BasicNavigator');

interface Props {
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  onLayout?: (event: LayoutChangeEvent) => void;
}

/**
 * Usage:
 * BasicNavigator is required in any react-native app that does not use react-navigation/react-native-screens at the root level.
 *
 * Purpose:
 * This prevents an Android issue where back navigation from hardware back buttons or system back gesture
 * results in unexpected back navigation in react-native apps that are not at the top of the stack.
 * We must explicitly consume this Android event to prevent it from propagating to apps lower in the stack.
 * ref: https://subsplash.atlassian.net/browse/MED-3832
 * @param children
 * @returns children
 */
export default function BasicNavigator({
  children,
  style,
  onLayout,
}: Props): JSX.Element {
  useEffect(() => {
    const handleAndroidSystemBackPressEvent = () => {
      debug('handleAndroidSystemBackPressEvent');
      dismissReactNativeModal(); // dismiss react-native mini app

      return true;
    };

    BackHandler.addEventListener(
      'hardwareBackPress',
      handleAndroidSystemBackPressEvent
    );

    return () =>
      BackHandler.removeEventListener(
        'hardwareBackPress',
        handleAndroidSystemBackPressEvent
      );
  }, []);

  return (
    <View style={style} onLayout={onLayout}>
      {children}
    </View>
  );
}
